import { createContext, useContext } from 'react';

import { createForm } from '@components/form';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { FieldMinHeightWrapper } from './common';
import type { IntegrationEndpoint, IntegrationEndpointVariable } from './integrations-types';

type Data = {};

const Form = createForm<Data>();

const idSeparator = '---';

const getFieldId = (name: string, field: string): keyof Data => {
  return `${name}${idSeparator}${field}` as keyof Data;
};

export const getFieldIds = (name: string) => {
  return {
    typeId: getFieldId(name, 'type'),
    optionsFromId: getFieldId(name, 'options-from'),
    labelId: getFieldId(name, 'label'),
    exampleId: getFieldId(name, 'example'),
    defaultValueId: getFieldId(name, 'default'),
    visibleId: getFieldId(name, 'visible'),
    requiredId: getFieldId(name, 'required'),
    formatId: getFieldId(name, 'format'),
    jqQueryId: getFieldId(name, 'jqQuery'),
  };
};

type EndpointType = IntegrationEndpoint<string> | IntegrationEndpoint;
export const endpointContext = createContext<EndpointType>({} as EndpointType);

type ValueFieldProps = {
  field: IntegrationEndpointVariable;
  form: typeof Form;
  id: keyof Data;
  defaultValue: any;
};

export const ExampleValueField = ({ field, form: Form, id, defaultValue }: ValueFieldProps) => {
  return (
    <ValueField id={id} field={field} form={Form} defaultValue={defaultValue} type="example" />
  );
};

type FileFieldProps = {
  field: Pick<IntegrationEndpointVariable, 'name' | 'example' | 'type'>;
  form: typeof Form;
  id: keyof Data;
};

const MultipartExampleFileField = ({ field, form: Form, id }: FileFieldProps) => {
  const endpoint = useContext(endpointContext);
  const exampleFile = endpoint.exampleFiles?.find((file) => file.variableName === field.name);
  const file = exampleFile
    ? { id: exampleFile.id, name: exampleFile.name, src: exampleFile.src }
    : undefined;
  const url = file ? new URL(file.src) : undefined;
  const key = `${url ? url.pathname.split('/').pop() : undefined}`;
  return (
    <Form.FileField
      id={id}
      key={key}
      label={field.name}
      hideLabel
      variant="small"
      defaultValue={file}
    />
  );
};

const MultipartDefaultFileField = ({ field, form: Form, id }: FileFieldProps) => {
  const endpoint = useContext(endpointContext);
  const defaultFile = endpoint.defaultFiles?.find((file) => file.variableName === field.name);
  const file = defaultFile
    ? { id: defaultFile.id, name: defaultFile.name, src: defaultFile.src }
    : undefined;
  const url = file ? new URL(file.src) : undefined;
  const key = `${url ? url.pathname.split('/').pop() : undefined}`;
  return (
    <Form.FileField
      id={id}
      key={key}
      label={field.name}
      hideLabel
      variant="small"
      defaultValue={file}
    />
  );
};

export const DefaultValueField = ({ field, form: Form, id, defaultValue }: ValueFieldProps) => {
  return (
    <ValueField id={id} field={field} form={Form} defaultValue={defaultValue} type="default" />
  );
};

const ValueField = ({
  field,
  form: Form,
  id,
  defaultValue,
  type,
}: ValueFieldProps & { type: 'example' | 'default' }) => {
  const { t } = useScopedTranslate(locale);

  switch (field.type) {
    case 'number':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={['number']}
          defaultValue={(defaultValue ?? '').toString()}
        />
      );
    case 'decimal':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={['decimal']}
          defaultValue={(defaultValue ?? '').toString()}
        />
      );
    case 'select':
      return (
        <Form.Select
          id={id}
          label={field.name}
          options={field.options ?? []}
          hideLabel
          defaultValue={defaultValue}
        />
      );
    case 'date':
      return <Form.DatePicker id={id} label={field.name} hideLabel defaultValue={defaultValue} />;
    case 'email':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={['email']}
          defaultValue={defaultValue ?? ''}
        />
      );
    case 'file':
      return type === 'default' ? (
        <MultipartDefaultFileField id={id} field={field} form={Form} />
      ) : (
        <MultipartExampleFileField id={id} field={field} form={Form} />
      );
    case 'boolean':
      return (
        <FieldMinHeightWrapper>
          <Form.Switch
            id={id}
            label={field.name}
            labelVisible={false}
            defaultValue={defaultValue ?? false}
          />
        </FieldMinHeightWrapper>
      );
    case 'array':
      return <></>;
    case 'array_of_strings': {
      const fieldOptions = field.options ?? [];
      const hasOptionsList = Boolean(field.options_from || fieldOptions.length);
      if (hasOptionsList) {
        const selectedValues = Array.isArray(defaultValue) ? defaultValue : [];
        const selectedOptions = selectedValues
          .map((selectedValue) => {
            return fieldOptions.find((option) => option.value === selectedValue);
          })
          .filter((option): option is { value: string; label: string } => option !== undefined);
        return (
          <Form.SelectMulti
            id={id}
            label=""
            options={fieldOptions}
            defaultValue={selectedOptions}
            menuPortalTarget={document.body}
          />
        );
      }
      const textValue = Array.isArray(defaultValue) ? defaultValue.join('\n') : defaultValue ?? '';
      return (
        <Form.Textarea
          id={id}
          label={field.name}
          hideLabel
          defaultValue={textValue}
          style={arrayFieldTextareaStyle}
          helpText={t('oneItemPerLine')}
        />
      );
    }
    case 'secret':
      return (
        <Form.TextField
          id={id}
          label={field.name}
          hideLabel
          validators={type === 'default' && !field.has_secret_value ? ['required'] : undefined}
          placeholder={
            type === 'example'
              ? t('secretExamplePlaceholder')
              : field.has_secret_value
              ? t('secretLeaveEmpty')
              : undefined
          }
        />
      );
    case 'paragraph':
    case 'text':
    default:
      return (
        <Form.TextField id={id} label={field.name} hideLabel defaultValue={defaultValue ?? ''} />
      );
  }
};

const arrayFieldTextareaStyle = {
  minHeight: pxToRem(38),
  padding: `${pxToRem(8)} ${pxToRem(12)}`,
  boxSizing: 'border-box',
  fieldSizing: 'content',
} as const;

const locale = {
  en: {
    oneItemPerLine: 'One item per line',
    secretLeaveEmpty: 'Leave empty to keep current',
    secretExamplePlaceholder: 'Leave empty to use stored credential',
  },
};
